export const getProjectWorkspacesListPageUrl = (projectId: string) =>
  `/project/${projectId}/Workspaces?projectId=${projectId}`;

// Project-level "Folders" page — the multi-space file tree (all Spaces of the project as roots).

export const getProjectRiskManagementListPageUrl = (projectId: string) =>
  `/project/${projectId}/riskManagement?projectId=${projectId}`;

// Project-level "Folders" page (multi-space file tree). Replaces getProjectWorkspacePageUrl: the Space now
// lives in the #folderPath hash (its id is the first segment) instead of the URL path.
// `searchText` drives the file manager's pre-search, which is space-specific — so whenever a searchText is
// passed, we also stamp `spaceId` into the query params so the pre-search runs inside that particular Space
// (in addition to the Space being focused via the #folderPath hash).
export const getProjectFoldersPageUrl = (
  projectId: string,
  spaceId?: string,
  path: string[] = [],
  searchText: string = "",
) => {
  const searchParams = new URLSearchParams();
  searchParams.set("projectId", projectId);
  if (searchText) {
    searchParams.set("searchText", searchText);
    if (spaceId) {
      searchParams.set("spaceId", spaceId);
    }
  }
  let url = `/project/${projectId}/folders?${searchParams.toString()}`;

  const filteredPath = path.filter((val) => val && val != "0");
  if (spaceId) {
    const folderPath = encodeURIComponent(`/${[spaceId, ...filteredPath].join("/")}/`);
    url += `#folderPath=${folderPath}`;
  }

  return url;
};

export const getProjectIssuesPageUrl = (projectId: string) => `/project/${projectId}/issues?projectId=${projectId}`;
export const getProjectPhotosPageUrl = (projectId: string) => `/project/${projectId}/photos?projectId=${projectId}`;
export const getProjectFilesPageUrl = (projectId: string) => `/project/${projectId}/listFiles?projectId=${projectId}`;
export const getProjectTransmittalsPageUrl = (projectId: string) =>
  `/project/${projectId}/listTransmittals?projectId=${projectId}`;
export const getSingleTransmittalPageUrl = (projectId: string, transmittalId: string) =>
  `/project/${projectId}/listTransmittals/${transmittalId}?projectId=${projectId}`;
export const getProjectTasksPageUrl = (projectId: string, taskId?: string) => {
  if (taskId) {
    return `/project/${projectId}/listTask/${taskId}?projectId=${projectId}`;
  }
  return `/project/${projectId}/listTask?projectId=${projectId}`;
};
export const getProjectEstimatesPageUrl = (projectId: string) =>
  `/project/${projectId}/EstimateList?projectId=${projectId}`;
export const getEstimatePageUrl = (projectId: string, estimateId: string) =>
  `/project/${projectId}/estimate/${estimateId}?projectId=${projectId}`;
export const getProjectAssetsPageUrl = (projectId: string) => `/project/${projectId}/assetsList?projectId=${projectId}`;
export const getAssetRelatedDocumentsPageUrl = (projectId: string, assetId: string, assetTag: string) =>
  `/project/${projectId}/assetsList/${assetId}/relatedDocuments?projectId=${projectId}&assetTag=${encodeURIComponent(assetTag)}`;
export const getProjectSchedulesPageUrl = (projectId: string) =>
  `/project/${projectId}/scheduleList?projectId=${projectId}`;
export const getProjectFormsPageUrl = (projectId: string, moduleName: string) =>
  `/project/${projectId}/formsList/${moduleName.toLowerCase()}?selectedTab=list&projectId=${projectId}`;
export const getProjectDashboardPageUrl = (projectId: string | undefined) =>
  `/project/${projectId}/projectdashboard?projectId=${projectId}`;
export const getPortalProjectsUrl = (projectId: string) => `/portalSettings/projects/${projectId}/projectUsers`;
export const getUserProjects = (userId: string) => `/portalSettings/users/${userId}/userProjects`;
export const getProjectMailboxPageUrl = (projectId: string) => `/project/${projectId}/mails?projectId=${projectId}`;
export const getProjectReviewsPageUrl = (projectId: string) => `/reviews?projectId=${projectId}&type=active`;
export const getProjectContractPageUrl = (projectId: string) =>
  `/project/${projectId}/contractsList?projectId=${projectId}`;
export const getReviewUrl = (projectId: string, spaceId: string, reviewId: string) =>
  `/review?reviewId=${reviewId}&spaceId=${spaceId}&projectId=${projectId}`;
export const getProjectGeneralClashPageUrl = (projectId: string) =>
  `/project/${projectId}/clashTests?projectId=${projectId}`;
export const getProjectTemplatesUrl = (projectTemplateId?: string) => {
  if (projectTemplateId) {
    return `/portalSettings/projectTemplates/${projectTemplateId}`;
  } else {
    return `/portalSettings/projectTemplates`;
  }
};
export const getCostDatabasesUrl = () => "/portalSettings/costDatabases";
export const getClashDetailsPageUrl = (projectId: string, clashId: string) =>
  `/project/${projectId}/clashDetails/${clashId}?projectId=${projectId}`;

export const getCreateTaskPageUrl = (projectId: string, extraSearchParams?: string) => {
  if (extraSearchParams) {
    return `/project/${projectId}/createTasks?projectId=${projectId}&${extraSearchParams}`;
  }
  return `/project/${projectId}/createTasks?projectId=${projectId}`;
};
export const getEditTaskPageUrl = (projectId: string, taskId: string, extraSearchParams?: string) => {
  if (extraSearchParams) {
    return `/project/${projectId}/editTask/${taskId}?projectId=${projectId}&${extraSearchParams}`;
  }
  return `/project/${projectId}/editTask/${taskId}?projectId=${projectId}`;
};

/**
 * Every id is optional because callers pass route params (`useParams()` values are
 * `string | undefined`). KNOWN GAP, preserved verbatim in all five builders below: a missing
 * `projectId` is still written into the query string, where it stringifies to `"undefined"`.
 */
type EstimateUrlProps = {
  projectId?: string;
  estimateList?: boolean;
  estimateId?: string;
  versionId?: string;
};

export const getEstimateUrl = (props: EstimateUrlProps) => {
  const { estimateList, projectId, estimateId, versionId } = props;
  const searchParams = new URLSearchParams("");
  let location = `/project/${projectId}`;
  if (estimateList) {
    location += "/EstimateList";
  }
  if (estimateId) {
    location += `/estimate/${estimateId}`;

    if (versionId) {
      location += `/version/${versionId}`;
    }
  }

  searchParams.set("projectId", projectId!);

  return `${location}?${searchParams.toString()}`;
};

type ScheduleUrlProps = { projectId?: string; scheduleId?: string; selectedTab?: string };

export const getScheduleUrl = (props: ScheduleUrlProps) => {
  const { projectId, scheduleId, selectedTab = "Gantt" } = props;
  const searchParams = new URLSearchParams("");
  const location = `/project/${projectId}/schedule/${scheduleId}/scheduleDetails`;

  searchParams.set("selectedTab", selectedTab);
  searchParams.set("projectId", projectId!);

  return `${location}?${searchParams.toString()}`;
};

export const getContractListUrl = (url: string, props: { projectId?: string; selectedTab?: string }) => {
  const { projectId, selectedTab } = props;
  const searchParams = new URLSearchParams("");

  if (projectId) {
    searchParams.set("projectId", projectId);
  }
  if (selectedTab) {
    searchParams.set("selectedTab", selectedTab);
  }

  return `${url}?${searchParams.toString()}`;
};

export const getProjectSettingsPageUrl = ({
  projectId,
  tab,
  extraSearchParams = {},
}: {
  projectId: string;
  tab: string;
  extraSearchParams?: Record<string, string>;
}) => {
  const params = new URLSearchParams({
    projectId,
    tab,
    ...extraSearchParams,
  });
  return `/project/${projectId}/setting?${params.toString()}`;
};

type ContractDetailUrlProps = {
  projectId?: string;
  /** `null` when a form instance is not linked to a contract. */
  contractId?: string | null;
  selectedTab?: string;
  /**
   * Callers hand this in as `ObjectIdString | null` (route/instance data) and, from the contract
   * version grid, as `string | boolean` — that row type declares `versionId?: string | boolean`.
   * Widened to what the callers actually pass; only a truthy value ever reaches the query string.
   */
  versionId?: string | boolean | null;
};

export const getContractDetailUrl = (props: ContractDetailUrlProps) => {
  const { selectedTab, projectId, contractId, versionId } = props;

  const url = `/project/${projectId}/contract/${contractId}`;
  const searchParams = new URLSearchParams("");

  if (projectId) {
    searchParams.set("projectId", projectId);
  }
  if (selectedTab) {
    searchParams.set("selectedTab", selectedTab);
  }
  if (versionId) {
    // Truthy here, so never `false`/`null`; the grid's `boolean` variant is a row-type artefact, and
    // `String` on the remaining `string | true` leaves a real id untouched.
    searchParams.set("versionId", String(versionId));
  }

  return `${url}?${searchParams.toString()}`;
};

export const getFormsListUrl = (props: { projectId?: string; selectedTab?: string; resourceType?: string }) => {
  const { selectedTab, projectId, resourceType } = props;

  const url = `/project/${projectId}/formsList/${resourceType?.toLowerCase()}`;
  const searchParams = new URLSearchParams("");

  if (projectId) {
    searchParams.set("projectId", projectId);
  }
  if (selectedTab) {
    searchParams.set("selectedTab", selectedTab);
  }

  return `${url}?${searchParams.toString()}`;
};

export const getProjectPageUrl = (searchParams: string, typeProject: string, viewProject: string) => {
  const params = new URLSearchParams();

  if (searchParams) {
    params.set("search", searchParams);
  }

  if (typeProject) {
    params.set("type", typeProject);
  }
  if (viewProject) {
    params.set("view", viewProject);
  }

  return `/projects?${params.toString()}`;
};
